import { useEffect, useRef } from "react";
import L from "leaflet";
import "@/lib/leaflet-setup";
import { INSTALLATION_STATUS_LABELS } from "@/config/constants";
import type { MapTask, TaskStatus, TaskType, OrderType } from "@/types/supabase-helpers";
import { isRouteGeometry } from "@/types/supabase-helpers";

// Re-export types for external consumers
export type { MapTask as Task, TaskStatus, TaskType, OrderType };

interface LoadTasksMapProps {
  tasks: MapTask[];
  depotLocation: { lat: number; lng: number; name: string };
  routeGeometry?: unknown;
  onTaskClick?: (taskId: string) => void;
}

// Status to color mapping
const getStatusColor = (task: MapTask): string => {
  if (task.status === "completed") return "#22c55e"; // green-500
  if (task.status === "partial") return "#f97316"; // orange-500
  if (task.status === "skipped") return "#ef4444"; // red-500
  if (task.status === "cancelled") return "#6b7280"; // gray-500
  
  // Pending/in_progress - color by task type
  if (task.task_type === "installation" && task.order?.order_type === "pickup_work") {
    return "#14b8a6"; // teal-500
  }
  if (task.task_type === "installation" && task.order?.order_type === "field_work") {
    return "#a855f7"; // purple-500
  }
  if (task.task_type === "pickup") {
    return "#f97316"; // orange-500
  }
  return "#3b82f6"; // blue-500 (primary)
};

const getStatusLabel = (task: MapTask): string => {
  return INSTALLATION_STATUS_LABELS[task.status] || task.status;
};

const getTaskTypeLabel = (task: MapTask): string => {
  if (task.task_type === "installation" && task.order?.order_type === "pickup_work") {
    return "Palautus";
  }
  if (task.task_type === "installation" && task.order?.order_type === "field_work") {
    return "Kenttätyö";
  }
  if (task.task_type === "pickup") {
    return "Nouto";
  }
  if (task.task_type === "installation") {
    return "Asennus";
  }
  if (task.task_type === "delivery") {
    return "Toimitus";
  }
  return task.task_type;
};

export function LoadTasksMap({ tasks, depotLocation, routeGeometry, onTaskClick }: LoadTasksMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize map centered on depot
    const map = L.map(mapRef.current).setView([depotLocation.lat, depotLocation.lng], 10);
    mapInstanceRef.current = map;

    // Add tile layer
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [depotLocation.lat, depotLocation.lng]);

  // Update markers when tasks change
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remove existing markers and polylines
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker || layer instanceof L.Polyline) {
        map.removeLayer(layer);
      }
    });

    const bounds: L.LatLng[] = [];

    // Add depot marker (green house icon)
    const depotIcon = L.divIcon({
      className: "custom-depot-marker",
      html: `<div style="
        background-color: #16a34a;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/>
          <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        </svg>
      </div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      popupAnchor: [0, -18],
    });

    L.marker([depotLocation.lat, depotLocation.lng], { icon: depotIcon })
      .addTo(map)
      .bindPopup(`<div style="font-weight: 600;">Verstas</div><div style="color: #6b7280; font-size: 12px;">${depotLocation.name}</div>`);

    bounds.push(L.latLng(depotLocation.lat, depotLocation.lng));

    // Add task markers
    tasks.forEach((task, index) => {
      const lat = task.latitude ?? task.cemetery?.latitude;
      const lng = task.longitude ?? task.cemetery?.longitude;

      if (!lat || !lng) return;

      const color = getStatusColor(task);
      const position = task.load_position || index + 1;
      const isHandled = ["completed", "partial", "skipped", "cancelled"].includes(task.status);

      const taskIcon = L.divIcon({
        className: "custom-task-marker",
        html: `<div style="
          background-color: ${color};
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          color: white;
          font-size: 14px;
          opacity: ${isHandled ? 0.7 : 1};
        ">
          ${position}
        </div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        popupAnchor: [0, -16],
      });

      const customerName = task.order?.customer?.company_name 
        || [task.order?.customer?.first_name, task.order?.customer?.last_name].filter(Boolean).join(" ")
        || "Asiakas ei tiedossa";

      const marker = L.marker([lat, lng], { icon: taskIcon }).addTo(map);
      
      marker.bindPopup(`
          <div style="min-width: 160px;">
            <div style="font-weight: 600; margin-bottom: 2px;">${task.order?.order_number || "Tilaus"}</div>
            <div style="color: #6b7280; font-size: 12px; margin-bottom: 4px;">${customerName}</div>
            <div style="font-size: 12px; margin-bottom: 4px;">
              📍 ${task.cemetery?.name || "Kohde"}, ${task.cemetery?.city || ""}
            </div>
            <div style="display: flex; gap: 4px; margin-top: 6px; margin-bottom: 8px;">
              <span style="
                background: ${color}20;
                color: ${color};
                padding: 2px 8px;
                border-radius: 4px;
                font-size: 11px;
                font-weight: 500;
              ">${getTaskTypeLabel(task)}</span>
              <span style="
                background: ${color}20;
                color: ${color};
                padding: 2px 8px;
                border-radius: 4px;
                font-size: 11px;
                font-weight: 500;
              ">${getStatusLabel(task)}</span>
            </div>
            <button 
              id="task-btn-${task.id}" 
              style="
                width: 100%;
                padding: 6px 12px;
                background: hsl(var(--primary));
                color: hsl(var(--primary-foreground));
                border: none;
                border-radius: 6px;
                font-size: 12px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 4px;
              "
            >
              👁 Näytä tiedot
            </button>
          </div>
        `);

      marker.on("popupopen", () => {
        const btn = document.getElementById(`task-btn-${task.id}`);
        if (btn && onTaskClick) {
          btn.onclick = () => {
            onTaskClick(task.id);
            marker.closePopup();
          };
        }
      });

      bounds.push(L.latLng(lat, lng));
    });

    // Draw route line if geometry is available
    if (routeGeometry && isRouteGeometry(routeGeometry)) {
      // GeoJSON uses [lng, lat], Leaflet uses [lat, lng]
      const routeCoords = routeGeometry.coordinates.map(coord => L.latLng(coord[1], coord[0]));
      L.polyline(routeCoords, {
        color: "#3b82f6",
        weight: 4,
        opacity: 0.7,
        dashArray: "10, 10",
      }).addTo(map);
    }

    // Fit bounds to show all markers
    if (bounds.length > 1) {
      map.fitBounds(L.latLngBounds(bounds), { padding: [30, 30], maxZoom: 12 });
    } else if (bounds.length === 1) {
      map.setView(bounds[0], 12);
    }
  }, [tasks, depotLocation, routeGeometry, onTaskClick]);

  return <div ref={mapRef} className="h-full w-full rounded-lg isolate" />;
}
